import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import GuardDevicesTable from "./GuardDevicesTable";
import GuardDeviceDrillIn from "./GuardDeviceDrillIn";
import GuardReleasePolicyCard from "./GuardReleasePolicyCard";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(4),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  title: {
    fontSize: "1.35rem",
    fontWeight: 700,
  },
  subtitle: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  backButton: {
    marginRight: theme.spacing(1),
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
  errorBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  deviceIdDisplay: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  search: {
    minWidth: 220,
  },
}));

export default function GuardDashboard({
  loading,
  error,
  devices,
  selectedDevice,
  selectedDeviceEvents,
  versionDistribution,
  releases,
  releasePolicy,
  releaseEnvOverrides,
  onSelectDevice,
  onClearDevice,
  onRefresh,
  onRefreshReleases,
  onSaveReleasePolicy,
  onRevoke,
  onSearch,
}) {
  const classes = useStyles();

  return (
    <Container maxWidth="lg" className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          {selectedDevice && (
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={onClearDevice}
              className={classes.backButton}
            >
              Back
            </Button>
          )}
          <Typography className={classes.title}>Ouro Guard</Typography>
          {selectedDevice && (
            <Typography className={classes.deviceIdDisplay}>
              {selectedDevice.deviceId}
            </Typography>
          )}
        </div>
        {!selectedDevice && (
          <div className={classes.toolbar}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search user or device ID…"
              className={classes.search}
              onChange={(e) => onSearch(e.target.value)}
            />
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>
        )}
      </div>

      {!selectedDevice && (
        <Typography className={classes.subtitle}>
          Enrolled Ouro Guard devices — posture, heartbeat status, and security events per device. Super-admin only.
        </Typography>
      )}

      {error && (
        <div className={classes.errorBox}>
          <Typography variant="body2">{error}</Typography>
        </div>
      )}

      {loading && !devices.length && !selectedDevice ? (
        <div className={classes.loading}>
          <CircularProgress size={32} />
        </div>
      ) : selectedDevice ? (
        <GuardDeviceDrillIn
          device={selectedDevice}
          events={selectedDeviceEvents}
          onRevoke={onRevoke}
        />
      ) : (
        <>
          <GuardReleasePolicyCard
            releases={releases}
            policy={releasePolicy}
            envOverrides={releaseEnvOverrides}
            loading={loading}
            onRefresh={onRefreshReleases}
            onSave={onSaveReleasePolicy}
          />
          <GuardDevicesTable
            devices={devices}
            versionDistribution={versionDistribution}
            onSelectDevice={onSelectDevice}
          />
        </>
      )}
    </Container>
  );
}
